import React from "react";
import { Tabs as TabsUI, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import { isEmpty, keys } from "lodash";

// Separate rounded tabs on a transparent strip, instead of the segmented control.
// Placed before fullWidth and the state classes so those still win.
const PILL = {
  list: "w-fit flex items-center gap-2 !bg-transparent !border-0 !shadow-none",
  trigger:
    "px-[6px] py-1 w-fit rounded-lg border-[0.5px] border-transparent data-[state=active]:border-border-badge-selected",
  text: "text-base font-normal text-primary"
};

export type tabsPropsType = {
  options: {
    [key: string]: string | JSX.Element;
  };
  defaultValue?: string;
  value?: string;
  valueSelected?: (value: string) => void;
  className?: string;
  containerClassName?: string;
  textClassName?: string;
  dataTest?: string;
  size?: "sm" | "lg";
  disabled?: boolean;
  readOnly?: boolean;
  disabledOptions?: string[];
  isPositiveNegative?: boolean;
  fullWidth?: boolean;
  tabClassName?: string;
  variant?: "default" | "pill";
};

const Tabs: React.FC<tabsPropsType> = ({
  options,
  defaultValue,
  value,
  valueSelected,
  className,
  containerClassName,
  textClassName,
  dataTest,
  size = "sm",
  disabled = false,
  readOnly = false,
  disabledOptions,
  isPositiveNegative = false,
  fullWidth = false,
  variant = "default"
}) => {
  const isPill = variant === "pill";
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState(defaultValue || "");
  const currentValue = isControlled ? value : internalValue;
  const isOptionDisabled = (optionValue: string) =>
    disabledOptions?.includes(optionValue) ?? false;

  // Track the timestamp of the last re-click to skip subsequent handleValueChange calls
  const lastReClickTimeRef = React.useRef<number>(0);

  const handleValueChange = (newValue: string) => {
    // pointer-events-none stops the mouse only; Radix activates a tab on arrow
    // keys through this same handler, so read-only has to be enforced here too.
    if (readOnly || isOptionDisabled(newValue)) {
      return;
    }

    const now = Date.now();
    const timeSinceReClick = now - lastReClickTimeRef.current;

    // Skip if this is within 100ms of a re-click (handled by onPointerDown)
    if (timeSinceReClick < 100) {
      return;
    }

    if (!isControlled) {
      setInternalValue(newValue);
    }
    valueSelected && valueSelected(newValue);
  };

  return (
    <TabsUI
      value={currentValue}
      onValueChange={handleValueChange}
      data-test={dataTest}
      className={cn(
        fullWidth && "w-full",
        // Read-only has no visual state of its own: the selection is shown as
        // normal and input is refused.
        readOnly && "pointer-events-none"
      )}
    >
      <TabsList
        className={cn(
          "p-[2px] gap-[2px] rounded-lg shadow-field",
          disabled
            ? "border-border-field-disabled"
            : "border-border-field-default",
          size === "sm" ? "h-8" : "h-9",
          isPill && PILL.list,
          fullWidth && "w-full",
          containerClassName
        )}
      >
        {keys(options).map((value: string, index) => (
          <TabsTrigger
            key={value}
            value={value}
            data-test={`${value}-tab`}
            onPointerDown={() => {
              if (isOptionDisabled(value)) {
                return;
              }
              // Detect re-clicks by checking if the clicked value matches current value
              // onPointerDown fires before Radix updates state, so we get accurate detection
              const isReClick = currentValue && value === currentValue;

              // Only handle re-clicks here (when Radix won't trigger handleValueChange)
              // For normal clicks, let Radix's handleValueChange handle it
              if (isReClick && valueSelected) {
                lastReClickTimeRef.current = Date.now();
                valueSelected(value);
              }
            }}
            className={cn(
              "py-1 rounded-[6px]",
              isOptionDisabled(value)
                ? "cursor-default"
                : "data-[state=active]:bg-badges-selected hover:bg-surface-hover-soft",
              size === "sm" ? "px-1" : "px-2",
              fullWidth && "flex-1",
              isPill && PILL.trigger,
              disabled ? "data-[state=active]:bg-badges-disabled " : "",
              isPositiveNegative
                ? index === 0
                  ? "data-[state=active]:!text-positive data-[state=active]:!bg-badges-positive"
                  : "data-[state=active]:!text-error data-[state=active]:!bg-badges-negative"
                : "",
              className
            )}
          >
            <Text
              variant={
                isEmpty(currentValue)
                  ? "body"
                  : currentValue === value
                    ? "label"
                    : "bodySecondary"
              }
              className={cn(
                isPill && PILL.text,
                disabled ? "!text-disabled" : "",
                isPositiveNegative && currentValue === value
                  ? index === 0
                    ? "text-positive "
                    : "text-error "
                  : "",
                textClassName
              )}
            >
              {options[value]}
            </Text>
          </TabsTrigger>
        ))}
      </TabsList>
    </TabsUI>
  );
};

export default Tabs;
